'use client';

import useSWR, { mutate } from 'swr';
import FilterBuilder from '../utils/filterBuilder';
import { fleetApi } from '../api/axiosClient';
import React from 'react';

const getTzParam = () => `?tz=${Math.abs(new Date().getTimezoneOffset() / 60)}`;

const fetcher = async ([url, payloadStr]) => {
  try {
    const payload = typeof payloadStr === 'string' ? JSON.parse(payloadStr) : payloadStr;
    const response = await fleetApi.post(url + getTzParam(), payload);
    return response;
  } catch (err) {
    console.error('API fetcher error:', err);
    throw err;
  }
};

export const revalidateFuelPriceHistoryList = () => {
  mutate((key) => Array.isArray(key) && key[0] === '/fuel-price-history/search');
};

/**
 * Searchable fields = columns shown in FuelPriceHistoryPage table.
 * key -> DB column (+ joined table) used by backend search.
 */
export const FUEL_PRICE_HISTORY_SEARCH_FIELDS = {
  company_name: { name: 'name', table_name: 'm_company' },
  license_plate: { name: 'license_plate', table_name: 'm_vehicle' },
  vehicle_name: { name: 'name', table_name: 'm_vehicle' },
  fuel_type_name: { name: 'name', table_name: 'm_fuel_type' },
  fuel_price_desc: { name: 'fuel_price_desc' },
  price: { name: 'price' },
  valid_at: { name: 'valid_at' }
};

const EMPTY_COLUMN = { logic_operator: 'like', name: 'id', operator: 'AND', value: '' };

export const buildFuelPriceHistorySearchPayload = ({
  start = 0,
  length = 10,
  searchKey = 'all',
  searchValue = '',
  companyId = ''
} = {}) => {
  const fb = new FilterBuilder()
    .setPaging(start, length)
    .addJoin('company', ['name'])
    .addJoin('vehicle', ['name', 'license_plate'])
    .addJoin('fuel_type', ['id', 'name', 'code', 'category', 'description', 'uom_type'])
    .setOrder(['id'], false);

  if (companyId) {
    const idStr = String(companyId);
    if (idStr.includes(',')) fb.in('company_id', idStr);
    else fb.equal('company_id', idStr);
  } else {
    fb.filterByCompany();
  }

  const query = String(searchValue ?? '').trim();
  if (query) {
    if (!searchKey || searchKey === 'all') {
      // OR search across every shown column (each column only once)
      Object.values(FUEL_PRICE_HISTORY_SEARCH_FIELDS).forEach((f) => fb.addFilter(f.name, query, f.table_name ?? null));
    } else {
      const f = FUEL_PRICE_HISTORY_SEARCH_FIELDS[searchKey];
      if (f) fb.like(f.name, query, 'AND', f.table_name ?? null);
    }
  }

  const built = fb.build();
  if (!built.columns || built.columns.length === 0) built.columns = [{ ...EMPTY_COLUMN }];
  return built;
};

const extractList = (resp) => {
  const body = resp ?? {};
  const rows = Array.isArray(body?.data) ? body.data : Array.isArray(body?.data?.data) ? body.data.data : Array.isArray(body) ? body : [];
  const total = body?.totalRecord ?? body?.data?.totalRecord ?? body?.totalRecords ?? rows.length;
  return { rows, total: Number(total) || 0 };
};

export const fetchAllFuelPriceHistory = async ({ searchKey, searchValue, companyId, total = 0 } = {}) => {
  const payload = buildFuelPriceHistorySearchPayload({ start: 0, length: total > 0 ? total : 9999, searchKey, searchValue, companyId });
  const resp = await fleetApi.post('/fuel-price-history/search' + getTzParam(), payload);
  return extractList(resp).rows;
};

export const useFuelPriceHistoryList = (page, rowsPerPage, searchKey, searchValue, companyId = '') => {
  const start = page * rowsPerPage;
  const payload = React.useMemo(
    () => buildFuelPriceHistorySearchPayload({ start, length: rowsPerPage, searchKey, searchValue, companyId }),
    [start, rowsPerPage, searchKey, searchValue, companyId]
  );

  const key = React.useMemo(() => ['/fuel-price-history/search', JSON.stringify(payload)], [payload]);

  const { data, error, isLoading } = useSWR(key, fetcher, {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    shouldRetryOnError: false,
    keepPreviousData: true,
    revalidateOnMount: true
  });

  const { rows, total } = extractList(data);
  return {
    data: rows,
    totalRecord: total,
    error,
    isLoading
  };
};

export const getFuelPriceHistoryById = async (id) => {
  if (id == null) throw new Error('id is required');
  try {
    const resp = await fleetApi.get(`/fuel-price-history/${id}${getTzParam()}`);
    if (resp?.data) return resp.data;

    // Fallback using search and FilterBuilder if direct GET fails or needs complex join
    const fb = new FilterBuilder().setPaging(0, 1).setOrder(['id'], false).filterByCompany().equal('id', String(id));
    const payload = fb.build();
    const response = await fleetApi.post(`/fuel-price-history/search${getTzParam()}`, payload);
    const dataArr = response?.data?.data ?? response?.data ?? (Array.isArray(response) ? response : null);

    return Array.isArray(dataArr) && dataArr.length > 0 ? dataArr[0] : null;
  } catch (err) {
    console.error('Failed to fetch fuel price history by id:', err);
    throw err;
  }
};

export const createFuelPriceHistory = async (formData = {}, isUpdateVehicle = undefined) => {
  try {
    const body = {
      ...formData
    };

    const updateParam = isUpdateVehicle !== undefined ? `&isUpdateVehicle=${isUpdateVehicle}` : '';
    const response = await fleetApi.post(`/fuel-price-history/new${getTzParam()}${updateParam}`, body);

    if (response?.data?.status === false || response?.data?.code >= 400 || response?.data?.code >= 403 || response?.data?.error) {
      throw { response: { data: response.data } };
    }
    revalidateFuelPriceHistoryList();
    return response.data;
  } catch (err) {
    console.error('Error creating fuel price history:', err);
    throw err;
  }
};

export const createFuelPriceHistoryBatch = async (formDataArray = [], isUpdateVehicle = undefined) => {
  try {
    // API endpoint /fuel-price-history/batch handles multiple data/vehicles
    const updateParam = isUpdateVehicle !== undefined ? `&isUpdateVehicle=${isUpdateVehicle}` : '';
    const response = await fleetApi.post(`/fuel-price-history/batch${getTzParam()}${updateParam}`, formDataArray);

    if (response?.data?.status === false || response?.data?.code >= 400 || response?.data?.code >= 403 || response?.data?.error) {
      throw { response: { data: response.data } };
    }
    revalidateFuelPriceHistoryList();
    return response.data;
  } catch (err) {
    console.error('Error creating fuel price history in batch:', err);
    throw err;
  }
};

export const updateFuelPriceHistory = async (id, formData = {}, isUpdateVehicle = undefined) => {
  if (id == null) throw new Error('id is required');
  try {
    const body = {
      ...formData
    };

    const updateParam = isUpdateVehicle !== undefined ? `&isUpdateVehicle=${isUpdateVehicle}` : '';
    const response = await fleetApi.put(`/fuel-price-history/edit/${id}${getTzParam()}${updateParam}`, body);

    if (response?.data?.status === false || response?.data?.code >= 400 || response?.data?.code >= 403 || response?.data?.error) {
      throw { response: { data: response.data } };
    }
    revalidateFuelPriceHistoryList();
    mutate(`/fuel-price-history/${id}`);
    return response.data;
  } catch (err) {
    console.error('Error updating fuel price history:', err);
    throw err;
  }
};

export const deleteFuelPriceHistory = async (id) => {
  if (id == null) throw new Error('id is required');
  try {
    const response = await fleetApi.delete(`/fuel-price-history/delete/${id}${getTzParam()}`);

    if (response?.data?.status === false || response?.data?.code >= 400 || response?.data?.error) {
      throw { response: { data: response.data } };
    }
    revalidateFuelPriceHistoryList();
    return response.data;
  } catch (err) {
    console.error('Error deleting fuel price history:', err);
    throw err;
  }
};
